/**
 * Validation utilities for Mikkel Kviss application
 */

const MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB (pre-compression)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validate room code format
 * @param {string} code - Room code to validate
 * @returns {boolean} True if valid (6 digits)
 */
export function isValidRoomCode(code) {
  if (!code || typeof code !== 'string') {
    return false;
  }

  // Check if exactly 6 digits
  const regex = /^[0-9]{6}$/;
  return regex.test(code.trim());
}

/**
 * Validate participant name
 * @param {string} name - Participant name to validate
 * @returns {boolean} True if valid (2-20 characters)
 */
export function isValidParticipantName(name) {
  if (!name || typeof name !== 'string') {
    return false;
  }

  const trimmedName = name.trim();
  return trimmedName.length >= 2 && trimmedName.length <= 20;
}

/**
 * Validate quiz structure
 * @param {Object} quiz - Quiz object to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function isValidQuiz(quiz) {
  const errors = [];

  if (!quiz || typeof quiz !== 'object') {
    return { valid: false, errors: ['Kviss må vere eit objekt'] };
  }

  // Validate title
  if (!quiz.title || typeof quiz.title !== 'string') {
    errors.push('Kvisstittel er påkravd');
  } else if (quiz.title.trim().length < 3) {
    errors.push('Kvisstittel må vere minst 3 teikn');
  }

  // Validate questions array
  if (!Array.isArray(quiz.questions)) {
    errors.push('Kviss må ha ei spørsmålsliste');
  } else if (quiz.questions.length < 1) {
    errors.push('Kviss må ha minst 1 spørsmål');
  } else {
    // Validate each question
    quiz.questions.forEach((question, index) => {
      if (!question.text || typeof question.text !== 'string' || question.text.trim().length === 0) {
        errors.push(`Spørsmål ${index + 1}: Spørsmålstekst er påkravd`);
      }

      // Validate answers
      const expectedAnswers = question.type === 'truefalse' ? 2 : 4;
      if (!Array.isArray(question.answers)) {
        errors.push(`Spørsmål ${index + 1}: Må ha ei svarliste`);
      } else if (question.answers.length < 2) {
        errors.push(`Spørsmål ${index + 1}: Må ha minst 2 svar`);
      } else if (question.type === 'truefalse' && question.answers.length !== 2) {
        errors.push(`Spørsmål ${index + 1}: Sant/usant-spørsmål må ha nøyaktig 2 svar`);
      } else {
        // Check that at least one answer is marked as correct
        const hasCorrectAnswer = question.answers.some(answer => answer.isCorrect === true);
        if (!hasCorrectAnswer) {
          errors.push(`Spørsmål ${index + 1}: Må ha minst 1 rett svar`);
        }

        // Validate each answer
        question.answers.forEach((answer, answerIndex) => {
          if (!answer.text || typeof answer.text !== 'string' || answer.text.trim().length === 0) {
            errors.push(`Spørsmål ${index + 1}, Svar ${answerIndex + 1}: Svartekst er påkravd`);
          }
          if (typeof answer.isCorrect !== 'boolean') {
            errors.push(`Spørsmål ${index + 1}, Svar ${answerIndex + 1}: isCorrect må vere ein boolean`);
          }
        });
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate image URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid URL or null/undefined
 */
export function isValidImageUrl(url) {
  if (!url) return true; // Optional field
  if (typeof url !== 'string') return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate image file for upload
 * @param {File} file - File to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function isValidImageFile(file) {
  if (!file) {
    return { valid: false, error: 'Ingen fil valt' };
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: 'Ugyldig filtype. Bruk JPG, PNG, GIF eller WebP.' };
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return { valid: false, error: 'Fila er for stor. Maks 15MB.' };
  }

  return { valid: true };
}
